// super-excalidraw: right-sidebar customisation. Drops the upstream
// Excalidraw+ promo tabs and adds three real tabs next to stock Library:
//   • Media — upload + manage personal images
//   • Presentation — frames-as-slides + playback
//   • Comments — per-diagram threads

import { DefaultSidebar, Sidebar } from "@excalidraw/excalidraw";
import {
  ImageIcon,
  presentationIcon,
  messageCircleIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";

import { CommentsTab } from "./CommentsTab";
import { MediaLibraryPanel } from "./MediaLibraryPanel";
import { PresentationTab } from "./PresentationTab";
import type { MediaItemDTO } from "../data/media";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import "./AppSidebar.scss";

export type AppSidebarProps = {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  diagramId: string | null;
  onInsertMedia?: (item: MediaItemDTO) => void;
  onStartPresentation?: (orderedFrameIds: string[]) => void;
};

export const AppSidebar = ({
  excalidrawAPI,
  diagramId,
  onInsertMedia,
  onStartPresentation,
}: AppSidebarProps) => {
  const { openSidebar } = useUIAppState();
  const onDefault = openSidebar?.name === "default";
  const mediaVisible = onDefault && openSidebar?.tab === "media";
  const presentationVisible =
    onDefault && openSidebar?.tab === "presentation";
  const commentsVisible = onDefault && openSidebar?.tab === "comments";

  return (
    <DefaultSidebar>
      <DefaultSidebar.TabTriggers>
        <Sidebar.TabTrigger
          tab="media"
          style={{ opacity: mediaVisible ? 1 : 0.5 }}
          title="Media library"
        >
          {ImageIcon}
        </Sidebar.TabTrigger>
        <Sidebar.TabTrigger
          tab="presentation"
          style={{ opacity: presentationVisible ? 1 : 0.5 }}
          title="Presentation"
        >
          {presentationIcon}
        </Sidebar.TabTrigger>
        <Sidebar.TabTrigger
          tab="comments"
          style={{ opacity: commentsVisible ? 1 : 0.5 }}
          title="Comments"
        >
          {messageCircleIcon}
        </Sidebar.TabTrigger>
      </DefaultSidebar.TabTriggers>
      <Sidebar.Tab tab="media">
        <MediaLibraryPanel
          visible={mediaVisible}
          onInsert={onInsertMedia}
        />
      </Sidebar.Tab>
      <Sidebar.Tab tab="presentation">
        <PresentationTab
          excalidrawAPI={excalidrawAPI}
          visible={presentationVisible}
          onStart={(ids) => onStartPresentation?.(ids)}
        />
      </Sidebar.Tab>
      <Sidebar.Tab tab="comments">
        <CommentsTab
          excalidrawAPI={excalidrawAPI}
          diagramId={diagramId}
          visible={commentsVisible}
        />
      </Sidebar.Tab>
    </DefaultSidebar>
  );
};
